import { CSSProperties, ReactNode } from 'react';
import { Color, Size, Styles } from '@vivli/shared/theme';
import { BadgeView } from '../../badges/badge.view';
import { defaultTabContainerStyle, topBlueTabContainerStyle } from './horizontal-nav-styles';

const innerContainerStyle: CSSProperties = {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    ...Styles.Transition.HOVER_OPACITY_TRANSITION
};

interface HorizontalNavTabProps {
    title: string;
    url: string;
    active: boolean;
    onTabClick: () => void;
    isDisabled?: boolean;
    isLoading?: boolean;
    count?: number;
    containedComponent?: ReactNode; //indicates whether the link for this tab has a containered
    // component that will display below the horizontal nav when this tab is active, which requires some special handling to ensure the tab and component display correctly together
    useTopStyle?: boolean; //use top style instead of default used in lower-level horizontal navs
}

export const HorizontalNavTabView = ({
    title,
    active = false,
    onTabClick,
    isDisabled,
    isLoading,
    count,
    useTopStyle,
    containedComponent
}: HorizontalNavTabProps) => {
    const activeTopStyle = useTopStyle && active;
    const transparentTopStyle = useTopStyle && !active && containedComponent;
    const testIdPrefix = useTopStyle ? 'top_' : 'default_';

    const tabContainerStyle: CSSProperties = {
        ...(useTopStyle ? topBlueTabContainerStyle : defaultTabContainerStyle),
        backgroundColor: activeTopStyle
            ? Color.VIVLI_ACTIVE_BLUE
            : useTopStyle
              ? containedComponent
                  ? Color.TRANSPARENT
                  : Color.VIVLI_DARK_BLUE
              : '#fff',
        cursor: isLoading ? 'wait' : isDisabled ? 'not-allowed' : 'pointer',
        zIndex: active ? 0 : 12
    };

    const textStyle: CSSProperties = {
        color: useTopStyle ? Color.WHITE : Color.VIVLI_LIGHT_BLUE,
        fontWeight: useTopStyle ? 545 : Size.FontWeight.Bold,
        fontSize: useTopStyle ? '14px' : Size.FontSize.Large,
        paddingBottom: Size.PADDING
    };

    const badgeStyle = (count: number): CSSProperties => ({
        backgroundColor: Color.VIVLI_LIGHT_BLUE,
        padding: 3,
        color: Color.WHITE, //no badge in top style, so no need for 2 values
        position: 'relative',
        bottom: -20,
        right: -15,
        height: 'initial',
        borderRadius: '3px',
        zIndex: 2
    });

    // Style for the active tab underline
    // but this is only for the default style,
    // not the top style, which has a different active tab design
    const tabBlockStyle: CSSProperties = {
        display: 'block',
        position: 'absolute',
        bottom: 0,
        left: 0,
        alignItems: 'center',
        width: '100%',
        height: active && !useTopStyle ? '4px' : '0px',
        backgroundColor: useTopStyle ? Color.WHITE : Color.VIVLI_LIGHT_BLUE,
        borderRadius: '2px'
    };

    return (
        <div
            style={tabContainerStyle}
            onClick={onTabClick}
            data-test-id={`${testIdPrefix}_horizontal_tab_${title?.replace(/ /g, '_')?.toLowerCase()}`}
        >
            <div style={innerContainerStyle}>
                {containedComponent ? (
                    <div
                        style={textStyle}
                        data-test-id={`${testIdPrefix}containered_horizontal_tab_${title?.replace(/ /g, '_')?.toLowerCase()}`}
                    >
                        {containedComponent}
                    </div>
                ) : (
                    <span style={textStyle}>{title}</span>
                )}
                <span style={tabBlockStyle} />
                {!useTopStyle && <BadgeView style={badgeStyle(count)} value={count} />}
            </div>
        </div>
    );
};
